import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { createResourceSetupService } from './service/createResourceSetupService';
import { IconButton, Tooltip, Box } from '@mui/material';
import { BugReport } from '@mui/icons-material';
import { useDebugLogger } from 'new/hooks/useDebugLogger';

interface TestSavePayloadProps {
  formType: string;
}

export const TestSavePayload: React.FC<TestSavePayloadProps> = ({ formType }) => {
  const debugLogger = useDebugLogger();
  // Single selector to get all form data at once
  const formData = useSelector((state: any) => state.forms[formType]?.data);

  // Extract values with fallbacks from the single selector result
  const formState = formData || {};
  const { selectedFeeds, sourceConfig, name, description, originalFeeds, discoveryResults } =
    formState;
  const availableFeeds = discoveryResults?.availableFeeds || [];

  // Extract other values from the same selector result
  const vendorFeedSpecs = formData?.vendorFeedSpecs || null;
  const sourceConfigById = formData?.sourceConfigById || {};
  const selectedConnection = formData?.selectedConnection;
  const resourceParams = selectedConnection ? sourceConfigById[selectedConnection] || [] : [];
  const templateVersionInfo = formData?.templateVersionInfo || null;

  const service = useMemo(() => createResourceSetupService(), []);

  const selectedFeedsCount = selectedFeeds ? Object.keys(selectedFeeds).length : 0;
  const hasSelectedFeeds = selectedFeedsCount > 0;

  const handleTestPayload = useCallback(() => {
    if (!hasSelectedFeeds) {
      debugLogger.debugLog(1, '=== DEBUG INFO ===');
      debugLogger.debugLog(1, 'No feeds selected yet. Select some feeds to test payload.');
      debugLogger.debugLog(1, 'Available feeds count:', Object.keys(selectedFeeds || {}).length);
      debugLogger.debugLog(1, 'Template version info:', templateVersionInfo);
      debugLogger.debugLog(1, '==================');
      return;
    }

    try {
      const payload = service.buildSourceInfo({
        sourceConfig,
        selectedFeeds,
        name,
        description,
        originalFeeds: originalFeeds || {},
        availableFeeds,
        vendorFeedSpecs,
        templateVersionInfo,
      });

      debugLogger.debugLog(0, '=== CURRENT SAVE PAYLOAD ===');
      debugLogger.debugLog(0, 'Full payload:', payload);
      debugLogger.debugLog(0, 'Feeds config:', payload.template_config?.feeds_config);
      debugLogger.debugLog(
        0,
        'Sample feed:',
        Object.values(payload.template_config?.feeds_config?.feeds || {})[0],
      );
      debugLogger.debugLog(0, 'Template version info:', templateVersionInfo);
      debugLogger.debugLog(1, '==========================');

      // Also log the selectedFeeds to see what we're working with
      // Debug level 2 logging
      debugLogger.debugLog(2, '=== SELECTED FEEDS ===');
      debugLogger.debugLog(2, 'Selected feeds:', selectedFeeds);
      debugLogger.debugLog(2, '=====================');

      // Log original feeds data for comparison
      debugLogger.debugLog(2, '=== ORIGINAL FEEDS ===');
      debugLogger.debugLog(2, 'Original feeds:', originalFeeds);
      debugLogger.debugLog(2, '=====================');

      // Log available feeds to see what's being processed
      debugLogger.debugLog(2, '=== AVAILABLE FEEDS ===');
      debugLogger.debugLog(2, 'Available feeds:', availableFeeds);
      debugLogger.debugLog(2, '======================');

      // Log vendorFeedSpecs to see vendor configuration
      debugLogger.debugLog(2, '=== VENDOR FEED SPECS ===');
      debugLogger.debugLog(2, 'Vendor feed specs:', vendorFeedSpecs);
      debugLogger.debugLog(
        2,
        'Object detection mode selector param:',
        vendorFeedSpecs?.ref_feed_options?.object_detection_mode_selector_param || 'NOT SUPPORTED',
      );
      debugLogger.debugLog(
        2,
        'Single schema param:',
        vendorFeedSpecs?.ref_feed_options?.single_schema_param || 'NOT SUPPORTED',
      );
      debugLogger.debugLog(
        2,
        'Force resync support:',
        vendorFeedSpecs?.ref_feed_options?.supports_force_resync || 'NOT SUPPORTED',
      );
      debugLogger.debugLog(2, '======================');

      // Log resource parameters to see what's available
      // Debug level 3 logging
      debugLogger.debugLog(3, '=== RESOURCE PARAMS ===');
      debugLogger.debugLog(3, 'Source config by ID:', sourceConfigById);
      debugLogger.debugLog(3, 'Selected connection:', selectedConnection);
      debugLogger.debugLog(3, 'Resource params:', resourceParams);
      debugLogger.debugLog(3, 'Resource params length:', resourceParams?.length || 0);
      if (resourceParams && Array.isArray(resourceParams)) {
        debugLogger.debugLog(3, 'Resource params with param_type:');
        resourceParams.forEach((param, index) => {
          debugLogger.debugLog(
            3,
            `  [${index}] name: ${param.name}, param_type: ${param.param_type}, value: ${param.value}`,
          );
        });
      }
      debugLogger.debugLog(3, '====================');
    } catch (error) {
      console.error('Error building payload:', error);
    }
  }, [debugLogger, service]);

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1000,
        opacity: hasSelectedFeeds ? 0.7 : 0.4,
        '&:hover': {
          opacity: 1,
        },
      }}
    >
      <Tooltip
        title={
          hasSelectedFeeds
            ? `Debug: Log payload for ${selectedFeedsCount} selected feeds`
            : `Debug: No feeds selected yet. Select some feeds to test payload.`
        }
        placement="left"
      >
        <IconButton
          onClick={handleTestPayload}
          disabled={!hasSelectedFeeds}
          sx={{
            backgroundColor: hasSelectedFeeds ? 'rgba(0, 0, 0, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            color: hasSelectedFeeds ? 'text.secondary' : 'text.disabled',
            '&:hover': {
              backgroundColor: hasSelectedFeeds ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
              color: hasSelectedFeeds ? 'text.primary' : 'text.disabled',
            },
            '&.Mui-disabled': {
              opacity: 0.4,
            },
          }}
          size="small"
        >
          <BugReport fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
